import React, { useMemo } from 'react';
import { Typography, Avatar, Tooltip } from '@material-ui/core';
import HistoryIcon from '@material-ui/icons/History';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import useStyles from './style';
import { formatDealReviewResetDisplayTime } from '../../utils';

const nameToInitials = (name) => {
    if (!name) return '?';
    return name
        .split(' ')
        .map((w) => w.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
};

const toTitleCase = (str) => {
    if (!str) return '';
    return str
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
};

/** Build map fieldName -> meta; first entry wins for duplicate `value` */
function useHistoryFieldMeta(historyFields) {
    return useMemo(() => {
        const map = new Map();
        if (!Array.isArray(historyFields)) return map;
        historyFields.forEach((f) => {
            if (f && f.value != null && String(f.value) !== '' && !map.has(f.value)) {
                map.set(f.value, f);
            }
        });
        return map;
    }, [historyFields]);
}

/** Format date values as DD/MM/YY (ISO YYYY-MM-DD without timezone shift). */
function formatHistoryDate(val) {
    if (val == null || val === '') return '';
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [y, m, d] = val.slice(0, 10).split('-').map(Number);
        if (!y || !m || !d) return String(val);
        const mm = String(m).padStart(2, '0');
        const dd = String(d).padStart(2, '0');
        return `${dd}/${mm}/${y}`;
    }
    const d = val instanceof Date ? val : new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
}

function toNumeric(val) {
    if (val == null || val === '') return null;
    const n = typeof val === 'number' ? val : Number(String(val).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
}

/** Resolve decimal places from roundoff / percentageDecimals config. */
function resolveDecimalPlaces(meta, fallback = 2) {
    const candidates = [meta?.roundoff, meta?.percentageDecimals, meta?.percentageDecimalPlaces];
    for (const candidate of candidates) {
        if (candidate == null || candidate === '') continue;
        const places = Number(candidate);
        if (Number.isFinite(places)) {
            return Math.max(0, Math.min(20, Math.floor(places)));
        }
    }
    return fallback;
}

/** Resolve display scalar when field type is object ({ value }). */
function unwrapObjectValue(raw) {
    if (raw != null && typeof raw === 'object' && !Array.isArray(raw) && !(raw instanceof Date)) {
        if (Object.prototype.hasOwnProperty.call(raw, 'value')) {
            return raw.value;
        }
    }
    return raw;
}

/** Format a history value using field meta (object / date / dollar / percentage). */
function formatDisplayValue(raw, meta) {
    if (raw == null || raw === '') return '';

    const type = meta?.type != null ? String(meta.type).toLowerCase() : '';
    const value = type === 'object' ? unwrapObjectValue(raw) : raw;
    if (value == null || value === '') return '';

    if (type === 'object') {
        return value != null && typeof value === 'object' && !(value instanceof Date)
            ? String(unwrapObjectValue(value) ?? '')
            : String(value);
    }

    if (type === 'date') {
        return formatHistoryDate(value);
    }

    const isPct = meta?.isPercentage === true || meta?.ispercentage === true;
    if (isPct) {
        const n = toNumeric(value);
        if (n == null) return String(value);
        const places = resolveDecimalPlaces(meta, 2);
        return `${n.toFixed(places)}%`;
    }

    const isDollar =
        meta?.isDoller === true || meta?.isDollar === true || meta?.isUSD === true;
    const isPound = meta?.isPound === true || meta?.isGBP === true;
    const isAmount = meta?.isAmount === true || isDollar || isPound;

    if ((type === 'number' || isAmount) && (isDollar || isPound || meta?.isAmount === true)) {
        const n = toNumeric(value);
        if (n == null) return String(value);
        const places = resolveDecimalPlaces(meta, 2);
        if (isPound) {
            try {
                return new Intl.NumberFormat('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                    minimumFractionDigits: places,
                    maximumFractionDigits: places,
                }).format(n);
            } catch {
                return `£${n.toLocaleString('en-GB', {
                    minimumFractionDigits: places,
                    maximumFractionDigits: places,
                })}`;
            }
        }
        if (isDollar) {
            try {
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: places,
                    maximumFractionDigits: places,
                }).format(n);
            } catch {
                return `$${n.toLocaleString('en-US', {
                    minimumFractionDigits: places,
                    maximumFractionDigits: places,
                })}`;
            }
        }
        return n.toLocaleString(undefined, {
            minimumFractionDigits: places,
            maximumFractionDigits: places,
        });
    }

    if (type === 'number') {
        const n = toNumeric(value);
        if (n == null) return String(value);
        const places = resolveDecimalPlaces(meta, 2);
        return n.toLocaleString(undefined, {
            minimumFractionDigits: places,
            maximumFractionDigits: places,
        });
    }

    return String(value);
}

const ActivityTimeline = ({ data, historyFields }) => {
    const classes = useStyles();
    const fieldMetaByKey = useHistoryFieldMeta(historyFields);

    const activities = useMemo(() => {
        if (!data?.data) return [];
        const raw = Array.isArray(data.data) ? data.data : [];
        return raw.filter((entry) => entry && (entry.name || entry.history));
    }, [data]);

    if (activities.length === 0) {
        return (
            <div className={classes.container}>
                <div className={classes.emptyState}>
                    <div className={classes.emptyIconWrapper}>
                        <HistoryIcon className={classes.emptyIcon} />
                    </div>
                    <Typography className={classes.emptyText}>No activity history yet</Typography>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <HistoryIcon className={classes.headerIcon} />
                <span className={classes.headerTitle}>Reset History</span>
                <span className={classes.headerCount}>{activities.length}</span>
            </div>

            <ul className={classes.timelineList}>
                {activities.map((entry, idx) => {
                    const historyObj =
                        Array.isArray(entry.history)
                            ? entry.history.reduce((acc, obj) => {
                                  if (obj && typeof obj === 'object') Object.assign(acc, obj);
                                  return acc;
                              }, {})
                            : typeof entry.history === 'object' && entry.history
                            ? entry.history
                            : {};

                    const flatChanges = Object.entries(historyObj).map(([key, value]) => ({
                        key,
                        value,
                    }));

                    return (
                        <li key={idx} className={classes.timelineItem}>
                            <div className={classes.dotColumn}>
                                <Avatar className={classes.avatar}>
                                    {nameToInitials(entry.name)}
                                </Avatar>
                                <div className={classes.connector} />
                            </div>

                            <div className={classes.contentCard}>
                                <div className={classes.cardHeader}>
                                    <div className={classes.userInfo}>
                                        <span className={classes.userName}>
                                            {entry.name || 'Unknown'}
                                        </span>
                                        <span className={classes.resetBadge}>
                                            <RotateLeftIcon className={classes.resetBadgeIcon} />
                                            Reset
                                        </span>
                                    </div>
                                    {entry.time && (
                                        <span className={classes.timeStamp}>
                                            {formatDealReviewResetDisplayTime(entry.time)}
                                        </span>
                                    )}
                                </div>

                                {flatChanges.length > 0 && (
                                    <div className={classes.changesContainer}>
                                        <div className={classes.changesLabel}>
                                            Values at time of reset
                                        </div>
                                        <div className={classes.changesGrid}>
                                            {flatChanges.map((change, cIdx) => {
                                                const meta = fieldMetaByKey.get(change.key);
                                                const label =
                                                    meta?.label && String(meta.label).trim() !== ''
                                                        ? meta.label
                                                        : toTitleCase(change.key);
                                                const hasVal =
                                                    change.value != null && change.value !== '';
                                                const displayVal = hasVal
                                                    ? formatDisplayValue(change.value, meta)
                                                    : '';
                                                const tooltipVal = hasVal
                                                    ? displayVal || String(change.value)
                                                    : '(empty)';
                                                return (
                                                    <Tooltip
                                                        key={cIdx}
                                                        title={`${label}: ${tooltipVal}`}
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <div className={classes.changeChip}>
                                                            <span className={classes.changeKey}>
                                                                {label}
                                                            </span>
                                                            <span
                                                                className={classes.changeSeparator}
                                                            >
                                                                :
                                                            </span>
                                                            <span
                                                                className={
                                                                    hasVal
                                                                        ? classes.changeVal
                                                                        : classes.changeValEmpty
                                                                }
                                                            >
                                                                {hasVal ? displayVal : '(empty)'}
                                                            </span>
                                                        </div>
                                                    </Tooltip>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default ActivityTimeline;
